import { getLocalTimeZone, today } from '@internationalized/date'

import type { TParametrosRotinaArquivo } from '@/domain/simulador/factories/concrete/horizontal/sacocv/rotina-arquivo-base'
import type { CalculadorFluxoDePagamentoSacocVParcelado } from '@/domain/simulador/factories/concrete/horizontal/sacocv-parcelado/calculador-fluxo-pagamento-sacocv-parcelado'
import type { CalculadorPrazoSacocVParcelado } from '@/domain/simulador/factories/concrete/horizontal/sacocv-parcelado/calculador-prazo-sacocv-parcelado'
import type {
  TSimulacaoCalculoPrazo,
  TSimulacaoCalculoRotinaArquivo,
  TSimulacaoCalculoValoresTotais,
  TSimulacaoFluxoPagamento,
} from '@/domain/simulador/factories/types/simulacao-calculo'
import { GerenciadorEntradasBase } from '@/domain/simulador/gerenciador-entradas-base'
import { GerenciadorIntermediariasBase } from '@/domain/simulador/gerenciador-intermediarias-base'
import { GerenciadorMensaisBase } from '@/domain/simulador/gerenciador-mensais-base'
import { GerenciadorPosConclusaoBase } from '@/domain/simulador/gerenciador-pos-conclusao-base'
import type { TParametrosDinamicosSimulacaoSimulador, TSimulacaoSchema } from '@/domain/simulador/types/simulacao.type'
import type { CalculadorFluxoDePagamentoBase } from '@/features/simulador/brasil/domain/calculador-fluxo-pagamento-base'
import type { CalculadorPrazoBase } from '@/features/simulador/brasil/domain/calculador-prazo-base'
import type { TRotinaArquivo } from '@/features/simulador/brasil/http/create-simulacao.service'
import type { TFormaDePagamentoEnum, TGetSimulacaoDuplicarResponse, TTipoAmortizacaoEnum } from '@/types/simulacao'
import type { CalculadoraDeValoresTotaisBase } from '@/utils/calculadora-valores-totais'
import type {
  TGrupoIntermediarias,
  TGrupoMensais,
  TGrupoParcelas,
} from '@/utils/calculadora-valores-totais/processador-valores-decimal'
import type { CalculadoraDeValoresTotaisSacocVParcelado } from '@/utils/calculadora-valores-totais-sacocv-parcelado'

export type TSimulacaoSacocVBase = {
  condicaoDePrecoId: string
  tabelaDePrecoId: string
  bloco: { idExterno: string; nome: string }
  produto: { idExterno: string; nome: string; metragem?: string }
  dataSaldoCondicaoPreco: string
  dataPrevisaoDeEntregaDoBloco: string
  tipoDeAmortizacao: TTipoAmortizacaoEnum
}

export type TGruposParcelaBase = {
  entradas: TGrupoParcelas
  mensais: TGrupoMensais[]
  intermediarias: TGrupoIntermediarias[]
  posConclusao?: TGrupoMensais
}

export abstract class SimulacaoSacocVBase {
  protected readonly dataSimulacao: string = today(getLocalTimeZone()).toString()
  abstract readonly entradas: GerenciadorEntradasBase
  abstract readonly mensais: GerenciadorMensaisBase
  abstract readonly intermediarias: GerenciadorIntermediariasBase
  abstract readonly posConclusao: GerenciadorPosConclusaoBase

  protected abstract readonly formaDePagamento: TFormaDePagamentoEnum

  protected criarSimulacaoBase(parametrosDinamicos: TParametrosDinamicosSimulacaoSimulador): TSimulacaoSacocVBase {
    const { condicaoDePreco, tabelaDePreco, bloco, produto } = parametrosDinamicos

    return {
      condicaoDePrecoId: condicaoDePreco.id,
      tabelaDePrecoId: tabelaDePreco.id,
      bloco: { idExterno: String(bloco.idExterno), nome: bloco.nome },
      produto: {
        ...produto,
        idExterno: String(produto.idExterno),
      },
      dataSaldoCondicaoPreco: condicaoDePreco.dataSaldo,
      dataPrevisaoDeEntregaDoBloco: condicaoDePreco.previsaoDeEntregaDoBloco,
      tipoDeAmortizacao: condicaoDePreco.tipoDeAmortizacao!,
    }
  }

  abstract criar(parametrosDinamicos: TParametrosDinamicosSimulacaoSimulador): TSimulacaoSchema<'SacocV'>

  abstract duplicar(
    simulacao: TGetSimulacaoDuplicarResponse,
    parametrosDinamicos: TParametrosDinamicosSimulacaoSimulador,
  ): TSimulacaoSchema<'SacocV'>
  abstract recalcular(
    parametrosDinamicos: TParametrosDinamicosSimulacaoSimulador,
  ): ReturnType<typeof this.criarSimulacaoBase>
  abstract calcularRotinaArquivo(
    simulacao: TSimulacaoCalculoRotinaArquivo,
    parametrosRotinaArquivo: TParametrosRotinaArquivo,
  ): TRotinaArquivo | null
  abstract calcularValoresTotais(
    simulacao: TSimulacaoCalculoValoresTotais,
    valorTabela?: number,
  ): CalculadoraDeValoresTotaisBase | CalculadoraDeValoresTotaisSacocVParcelado
  abstract calcularFluxoDePagamento(
    simulacao: TSimulacaoFluxoPagamento,
  ): CalculadorFluxoDePagamentoSacocVParcelado | CalculadorFluxoDePagamentoBase
  abstract calcularPrazo(simulacao: TSimulacaoCalculoPrazo): CalculadorPrazoBase | CalculadorPrazoSacocVParcelado
}
